import {
  AutoComplete,
  ButtonGeneral,
  ButtonSecondary,
  DateInputSplit,
  Dropdown,
  DropdownWidth,
  H5,
  Input,
  typographyRem,
} from '@by-miles/bymiles-styles'
import dayjs from 'dayjs'
import { useContext, useState } from 'react'

import { genericDateErrorCopy, numbersToLimitOptions } from '../../../../../constants'
import { AdditionalDriverInput, SetProposer } from '../../../../../generated/graphql'
import useDriverQueries from '../../../../../hooks/useDriverQueries'
import { ProviderContext } from '../../../../../Provider'
import { AccessibleFormWrapper } from '../../../../AccessibleFormWrapper'
import { GenericOverlay } from '../../../../Overlays'
import { ToolTip } from '../../../../ToolTip'
import { GenericSectionStyle } from '../../../GenericSectionStyle'
import { claimValidationRules } from '../../../utils/claimValidationRules'
import { DateErrorState, DateState } from '../../../utils/dateTypes'
import { YouThey } from '../../../utils/driverCopyChange'
import { handleDropdownState } from '../../../utils/handleDropdownState'
import { handlePrefilledDate } from '../../../utils/handlePrefilledDate'
import { handlePrefilledDropdown } from '../../../utils/handlePrefilledDropdown'
const { h2: H2 } = typographyRem

interface AddConvictionsModalProps {
  convictions: any
  setConvictions: (value: any) => void
  updatedDriverDetails: SetProposer
  setUpdatedDriverDetails: (value: SetProposer) => void
  updatedAdditionalDriver: AdditionalDriverInput
  setUpdatedAdditionalDriver: (value: AdditionalDriverInput) => void
  isEdit: boolean
  setIsEdit: (value: boolean) => void
  convictionNumber: number | undefined
  notAnsweredMessage: string
}

type AutoCompleteOption = {
  label: string
  value: string
}

export const AddConvictionsModal = ({
  convictions,
  setConvictions,
  updatedDriverDetails,
  setUpdatedDriverDetails,
  updatedAdditionalDriver,
  setUpdatedAdditionalDriver,
  isEdit,
  setIsEdit,
  convictionNumber = 1,
  notAnsweredMessage,
}: AddConvictionsModalProps) => {
  const {
    state: { selectedDriverIndex },
    handleCloseModal,
  } = useContext(ProviderContext)
  const { convictionsData } = useDriverQueries()

  const conviction = convictions[convictionNumber - 1]

  const [convictionSelected, setConvictionSelected] = useState<AutoCompleteOption | undefined>(
    isEdit ? convictionsData?.convictions?.find(obj => obj.value === conviction.code) : undefined,
  )
  const [convictionDate, setConvictionDate] = useState<DateState>(
    handlePrefilledDate(isEdit ? dayjs(conviction.date).add(1, 'day').toISOString() : ''),
  )
  const [convictionPoints, setConvictionPoints] = useState<number | undefined>(isEdit ? conviction.points : undefined)
  const [convictionMonths, setConvictionMonths] = useState<number | undefined>(isEdit ? conviction.ban : undefined)

  const [overlayErrors, setOverlayErrors] = useState({
    conviction: false,
    convictionDate: false,
    convictionPoints: false,
    convictionMonths: false,
  })

  const [dateError, setDateError] = useState<boolean>(false)
  const [convictionDateError, setConvictionDateError] = useState<string>('')

  const handleCheckDate = () => {
    if (!convictionDate.month || !convictionDate.year) {
      return
    } else if (dateError) {
      setConvictionDateError(genericDateErrorCopy)
    }
    if (convictionDate.year.toString()?.length === 4) {
      const month = convictionDate.month
      const year = convictionDate.year
      const d = new Date(year, month)
      const date = dayjs(d)
      if (date && date.isAfter(dayjs().add(1, 'M'))) {
        setConvictionDateError("That's odd, the date you have selected is in the future. Please check and try again.")
      } else if (date && date.isBefore(dayjs().subtract(5, 'year'))) {
        setConvictionDateError(
          "Provided this is the right date, you don't need to declare claims or convictions older than five years old, so you can cancel this conviction.",
        )
      }
    }
  }

  const handleDobErrors = (errors: DateErrorState) => {
    if (errors.month || errors.year) {
      setDateError(true)
    }
  }

  const handleAddIncident = () => {
    const month = convictionDate.month
    const year = convictionDate.year
    const incompleteFields = {
      conviction: convictionSelected === undefined,
      convictionDate: !month && !year,
      convictionPoints: convictionPoints === undefined,
      convictionMonths: typeof convictionMonths !== 'number',
    }

    if (Object.values(incompleteFields).filter(field => field)?.length > 0 || convictionDateError) {
      setOverlayErrors(incompleteFields)
      convictionDateError && setConvictionDateError(genericDateErrorCopy)
      return false
    } else {
      let date = dayjs()
      if (month && year) {
        date = dayjs.utc(`${year}-${month}-01`)
      }
      const convictionsArray = convictions || []
      const conviction = {
        code: convictionSelected?.value,
        date: date.toISOString(),
        points: convictionPoints,
        ban: convictionMonths,
      }

      if (isEdit) {
        convictionsArray[convictionNumber - 1] = { ...conviction }
      } else {
        convictionsArray.push(conviction)
      }

      setConvictions(convictionsArray)

      if (!selectedDriverIndex) {
        setUpdatedDriverDetails({ ...updatedDriverDetails, convictions: convictionsArray })
      } else {
        setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, convictions: convictionsArray })
      }

      setIsEdit(false)
      handleCloseModal('add_conviction')
    }
  }

  return (
    <GenericOverlay
      hideDismissIcon
      onClose={() => {}}
      overlay={
        <div>
          <H2>Conviction {convictions?.length && !isEdit ? convictions?.length + 1 : convictionNumber}.</H2>
          <GenericSectionStyle>
            <AccessibleFormWrapper className="generic-wrapper no-padding" onSubmit={handleAddIncident}>
              <div className="question-container">
                <div className="question-text-container">
                  <H5>{`What were ${YouThey()} convicted of?`}</H5>
                </div>
                <div className="auto-convictions-wrapper">
                  <AutoComplete
                    options={convictionsData?.convictions || []}
                    loading={false}
                    placeholder="Conviction type"
                    onChange={value => {
                      if (value) {
                        setConvictionSelected(value)
                        setOverlayErrors({ ...overlayErrors, conviction: false })
                      }
                    }}
                    preSelected={convictionSelected || undefined}
                    error={overlayErrors.conviction ? notAnsweredMessage : ''}
                    isValid={convictionSelected && !overlayErrors.conviction}
                    isOpen={false}
                    fullScreen={false}
                  />
                </div>
              </div>
              <div className="question-container" id="customPurchaseDate">
                <div className="question-text-container">
                  <H5>When did this conviction happen?</H5>
                </div>
                <DateInputSplit
                  dataCy="conviction-date-text-field"
                  showLabels={true}
                  validateOnBlur={false}
                  toggleErrorMessage={overlayErrors.convictionDate || !!convictionDateError}
                  dateStructure={['month', 'year']}
                  onBlur={() => handleCheckDate()}
                  errorMessage={overlayErrors.convictionDate ? notAnsweredMessage : convictionDateError}
                  onError={handleDobErrors}
                  onChange={(value: number, field: string) => {
                    setConvictionDateError('')
                    setDateError(false)
                    setOverlayErrors({ ...overlayErrors, convictionDate: false })
                    setConvictionDate({
                      ...convictionDate,
                      [field]: value,
                    })
                  }}
                  customValidation={claimValidationRules}
                  defaultValue={convictionDate}
                />
              </div>

              <div className="question-container">
                <div className="question-text-container">
                  <H5>{`How many points (if any) did ${YouThey()} receive on your driving licence?`}</H5>
                </div>
                <div className="input-info-container" id="policyStart">
                  <Dropdown
                    handleDropdownChange={option => {
                      setConvictionPoints(option.value)
                      setOverlayErrors({ ...overlayErrors, convictionPoints: false })
                    }}
                    isMobile={false}
                    options={numbersToLimitOptions(12)}
                    placeholder="Select"
                    states={handleDropdownState(
                      convictionPoints,
                      overlayErrors.convictionPoints ? notAnsweredMessage : '',
                    )}
                    errorMessage={overlayErrors.convictionPoints ? notAnsweredMessage : ''}
                    width={DropdownWidth.Large}
                    className="dropdown-medium-with-icon dropdown"
                    dataCy="inception-date-dropdown-list"
                    preFilled={handlePrefilledDropdown(numbersToLimitOptions(12), convictionPoints)}
                  />
                  <ToolTip
                    text={
                      <span>
                        {`To check how many penalty points ${YouThey()} have and when they'll be removed, the most up-to-date
                                    way to check is by using the DVLA's online tool here: `}
                        <a target="_blank" rel="noreferrer noopener" href="https://www.gov.uk/view-driving-licence">
                          www.gov.uk/view-driving-licence
                        </a>
                        . As the paper driving licence is no longer a valid format, any penalty point information
                        available on <span>your</span> physical paper licence may not be complete.
                      </span>
                    }
                    className="tool-tip tip-top-small"
                  />
                </div>
              </div>

              <div className="question-container">
                <div className="question-text-container">
                  <H5>{`For how many months (if any) were ${YouThey()} banned from driving?`}</H5>
                </div>
                <div className="input-info-container months-input" id="policyStart">
                  <Input
                    type="number"
                    name="number-of-months"
                    placeHolder="Months ban"
                    error={overlayErrors.convictionMonths ? notAnsweredMessage : ''}
                    onChange={(value: string) => {
                      setConvictionMonths(value ? parseInt(value) : undefined)
                      setOverlayErrors({ ...overlayErrors, convictionMonths: !value })
                    }}
                    valid={typeof convictionMonths === 'number' || false}
                    value={convictionMonths?.toString()}
                  />
                </div>
              </div>
            </AccessibleFormWrapper>
            <div className="additional-modal-button-container">
              <ButtonGeneral
                className="button-add"
                label="Add conviction"
                onClick={() => handleAddIncident()}
                dataCy="add-conviction-button"
                iconRight="IconCaretRight"
              />
              <ButtonSecondary
                className="button-cancel"
                label="Cancel"
                onClick={() => {
                  setIsEdit(false)
                  handleCloseModal('add_conviction')
                  if (convictions?.length < 1)
                    document.getElementById('input-has-motoring-convictions-toggle-no')?.click()
                }}
              />
            </div>
          </GenericSectionStyle>
        </div>
      }
    />
  )
}
